import React, { useState } from 'react';
import {
	Platform,
	StyleSheet,
	TextInput,
	TouchableOpacity,
	View,
} from 'react-native';
import I18n from '../../../components/shared/i18n/I18n.component';
import ImageIcon from '../../../components/shared/Icon/ImageIcon.component';
import ButtonV2 from '../../../components/shared/Buttons/ButtonV2.container';
import colors from '../../../config/colors';
import { i18nService } from '../../../services/i18n.service';

interface PasswordStepProps {
	idNumber: string;
	password: string;
	setPassword: (val: string) => void;
	onSubmit: () => void;
	onForgotPassword: () => void;
	onOneTimeCode: () => void;
	onBack: () => void;
	/** true when no employer requires 2FA - adds the "או" one-time-code alternative */
	showOneTimeCode: boolean;
	errorKey: string | null;
	loading: boolean;
}

export const PasswordStep = ({
	idNumber,
	password,
	setPassword,
	onSubmit,
	onForgotPassword,
	onOneTimeCode,
	onBack,
	showOneTimeCode,
	errorKey,
	loading,
}: PasswordStepProps) => {
	const [showPassword, setShowPassword] = useState(false);

	return (
		<>
			<I18n
				size={17}
				font='medium'
				color='primaryDarkBlue'
				style={styles.title}
			>
				signInV2.welcome.title
			</I18n>
			<I18n size={14} style={styles.subtitle} color='subDarkBlue'>
				signInV2.password.subtitle
			</I18n>
			{/* the id step is gone by now, so this pairs the password with the id for
				the keychain save prompt. native RN 0.63 has no autoComplete prop -
				iOS reads textContentType, Android autoCompleteType. hidden by
				transparent text rather than opacity 0 - a fully transparent field is skipped */}
			{Platform.OS !== 'web' && (
				<TextInput
					value={idNumber}
					autoComplete='username'
					autoCompleteType='username'
					textContentType='username'
					pointerEvents='none'
					caretHidden
					underlineColorAndroid='transparent'
					style={styles.hiddenUsername}
				/>
			)}
			<View
				style={[styles.inputContainer, errorKey ? styles.errorInput : {}]}
			>
				<TextInput
					style={styles.textInput}
					underlineColorAndroid='transparent'
					placeholder={i18nService.translate(
						'signInV2.password.placeholder',
					)}
					value={password}
					onChangeText={setPassword}
					secureTextEntry={!showPassword}
					autoCapitalize='none'
					autoCorrect={false}
					spellCheck={false}
					autoComplete='current-password'
					textContentType='password'
					returnKeyType='done'
					onSubmitEditing={onSubmit}
				/>
				<TouchableOpacity
					onPress={() => setShowPassword(state => !state)}
				>
					<ImageIcon
						name={showPassword ? 'hidePassword' : 'showPassword'}
						width={22}
						height={14}
					/>
				</TouchableOpacity>
			</View>
			{!!errorKey && (
				<View style={styles.errorRow}>
					<ImageIcon
						name={'alertCircle'}
						color={colors.failure}
						width={15}
						height={15}
						style={{ marginLeft: 4 }}
					/>
					<I18n color='failure' weight='normal' size={12}>
						{errorKey}
					</I18n>
				</View>
			)}
			<View style={styles.forgotRow}>
				<ButtonV2
					preset='link'
					size='medium'
					disabled={loading}
					onPress={onForgotPassword}
					// on native, the default width: '100%' wrapper stretches the button
					// across the row, so the text ends up centered instead of at the start					
					containerStyle={{ width: 'auto' }}
				>
					<I18n size={14} weight='400' color='titlePrimary'>
						signInV2.password.forgot
					</I18n>
				</ButtonV2>
			</View>
			<ButtonV2
				preset='primary'
				size='medium'
				disabled={loading}
				onPress={onSubmit}
				overrideStyle={{ width: '100%' }}
			>
				<I18n size={15} weight='normal' color='white'>
					signInV2.password.submit
				</I18n>
			</ButtonV2>
			{showOneTimeCode && (
				<>
					<View style={styles.dividerRow}>
						<View style={styles.dividerLine} />
						<I18n
							size={14}
							color='subDarkBlue'
							style={styles.dividerLabel}
						>
							signInV2.password.or
						</I18n>
						<View style={styles.dividerLine} />
					</View>
					<ButtonV2
						preset='filled'
						size='medium'
						disabled={loading}
						onPress={onOneTimeCode}
						overrideStyle={{ width: '100%' }}
					>
						<I18n size={15} weight='normal' color='white'>
							signInV2.password.oneTimeCode
						</I18n>
					</ButtonV2>
				</>
			)}
			<View style={styles.backRow}>
				<ButtonV2
					preset='link'
					size='medium'
					disabled={loading}
					onPress={onBack}
					overrideStyle={{ width: '100%' }}
				>
					<I18n size={14} weight='400' color='titlePrimary'>
						signInV2.back
					</I18n>
				</ButtonV2>
			</View>
		</>
	);
};

const styles = StyleSheet.create({
	title: {
		textAlign: 'center',
		marginBottom: 32,
		letterSpacing: 0.15,
	},
	subtitle: {
		textAlign: 'right',
		marginBottom: 12,
	},
	inputContainer: {
		flexDirection: 'row-reverse',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
		paddingHorizontal: 16,
		paddingVertical: 10,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: colors.clockGreyText,
		marginBottom: 8,
	},
	textInput: {
		textAlign: 'right',
		fontSize: 14,
		flex: 1,
		height: '100%',
		fontWeight: '500',
		letterSpacing: 0.25,
		color: colors.primaryDarkBlue,
		...Platform.select({
			web: {
				outlineWidth: 0,
				outlineStyle: 'none',
			},
		}),
	},
	errorInput: {
		borderColor: colors.failure,
	},
	hiddenUsername: {
		position: 'absolute',
		width: '100%',
		color: 'transparent',
	},
	errorRow: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		marginBottom: 8,
	},
	forgotRow: {
		flexDirection: 'row-reverse',
		marginBottom: 24,
	},
	dividerRow: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		marginVertical: 16,
	},
	dividerLine: {
		flex: 1,
		height: 1,
		backgroundColor: colors.greySeparator,
	},
	dividerLabel: {
		marginHorizontal: 12,
	},
	backRow: {
		marginTop: 12,
	},
});
